// The admission-tax vocabulary, in one place. (O-4509)
//
// The two engines (FinancialSummaryMath, SettlementMath) each carry their own
// copy of this logic and cannot import it: both are read off disk as source
// strings and evaluated in MiniRacer, so they have no module loader. Everything
// on the editor side imports from here instead, so at least the UI can't drift
// from itself — and when an engine copy changes, this file is the checklist of
// what else has to change with it.

// Set by the app on rows it manages, never by a user.
export const KIND_ADMISSION_TAX = 'admission_tax';
export const KIND_MANUAL_TAX_PROJECTION = 'manual_tax_projection';
export const KIND_FACILITY_MAINTENANCE_FEE = 'facility_maintenance_fee';
export const KIND_CHARITY_DEDUCTION = 'charity_deduction';

// Rows the app owns; a user may not mark these as the admission tax.
export const SYSTEM_KINDS = [
  KIND_FACILITY_MAINTENANCE_FEE,
  KIND_CHARITY_DEDUCTION,
  KIND_MANUAL_TAX_PROJECTION,
];

// Calc types that express a tax as a share of the gate. Used only to guess
// whether an offer already has a tax line of its own — never to decide what
// gets passed through, which is explicit-marking only.
export const NET_GROSS_BASIS_CALC_TYPES = [
  'percentage_of_net_gross',
  'tax_inclusive_percentage_of_net_gross',
];

export const TAX_CALC_TYPES = [
  'percentage_of_gross',
  'tax_inclusive_percentage',
  'percentage_of_net_gross',
  'tax_inclusive_percentage_of_net_gross',
];

// Deliberately liberal: "might this row be the venue's own tax line?" Used only
// to decide whether to skip the ManualTax forecast, so a false positive just
// means no forecast. Mirrors V3BudgetLineItem#looks_like_a_tax_line?.
export const looksLikeATaxLine = (item) => {
  if (!item) return false;
  if (item.kind === KIND_FACILITY_MAINTENANCE_FEE || item.kind === KIND_CHARITY_DEDUCTION) return false;
  return item.kind === KIND_ADMISSION_TAX || TAX_CALC_TYPES.includes(item.calc_type);
};

// Identity for comparing a row against its siblings. New rows carry a uuid
// before they have an id, persisted ones carry both.
const rowKey = (item) => item.uuid || (item.id != null ? String(item.id) : null);

// Whether a user may mark this row as the admission tax.
//
// Pass the section's rows to hide the control on every other row once one is
// marked: a budget can only have one admission tax (a unique index enforces it,
// and marking a second silently moves the marker off the first), so offering
// the control elsewhere advertises a choice the user does not have. The marked
// row always keeps it — that is how the marking is undone.
export const canMarkAsAdmissionTax = (item, rows = null) => {
  if (!item) return false;
  if (item.category !== 'BEFORE_ADJUSTED_DEDUCTIONS') return false;
  if (SYSTEM_KINDS.includes(item.kind)) return false;
  if (item.kind === KIND_ADMISSION_TAX) return true;
  if (!rows) return true;

  const key = rowKey(item);
  return !rows.some((row) => (
    row && row.kind === KIND_ADMISSION_TAX && rowKey(row) !== key
  ));
};
